import { useEffect, useState, forwardRef, useImperativeHandle } from "react";
import { Box, Typography, Switch } from "@mui/material";
import Section from "../Modal/Section";
import SmallInput from "../Inputs/SmallInput";
import ModalActions from "../Modal/Actions";
import { getDriverDetail, upsertDriver } from "../../api/drivers";

interface Driver {
  id: string;
  full_name: string;
  email: string;
  phone: string;
  status_id: number;
}

interface Props {
  driver: Driver | null;
  isCreating: boolean;
  onCancel: () => void;
  onSaved: () => void;
}

const defaultForm = {
  full_name: "",
  dni: "",
  phone: "",
  active: true,
};

export type DriverDetailHandle = {
  canLeave: () => boolean;
};

const DriverDetailPanel = forwardRef<DriverDetailHandle, Props>(
  function DriverDetailPanel(
    { driver, isCreating, onCancel, onSaved },
    ref
  ) {
    const [form, setForm] = useState(defaultForm);
    const [initialForm, setInitialForm] = useState(defaultForm);
    const [loadingDetail, setLoadingDetail] = useState(false);
    const [saving, setSaving] = useState(false);

    /* =========================
       CANCELAR
    ========================= */
    const handleCancel = () => {
      if (hasChanges()) {
        const confirmed = window.confirm(
          "Tienes cambios sin guardar. Si sales ahora, se perderán. ¿Deseas continuar?"
        );
        if (!confirmed) return;
      }
      onCancel();
    };

    /* =========================
       GUARDAR
    ========================= */
    const handleSubmit = async () => {
      try {
        setSaving(true);

        const payload: any = {
          fullname: form.full_name,
          dni: form.dni,
          phone: form.phone,
          status_id: form.active ? 1 : 2,
          action: isCreating ? "create" : "update",
        };

        if (!isCreating && driver) {
          payload.id = driver.id;
        }

        const res = await upsertDriver(payload);

        if (res.data.status) {
          setInitialForm(form);
          onSaved();
          onCancel();
        } else {
          alert(res.data.msg || "Error al guardar");
        }
      } catch (err) {
        console.error("Error guardando driver", err);
        alert("Error al guardar el motorizado");
      } finally {
        setSaving(false);
      }
    };

    /* =========================
       EFECTO PRINCIPAL
    ========================= */
    useEffect(() => {
      // 👉 CREAR
      if (isCreating) {
        setForm(defaultForm);
        setInitialForm(defaultForm);
        return;
      }

      // 👉 EDITAR
      if (!driver) return;

      const fetchDetail = async () => {
        try {
          setLoadingDetail(true);
          const res = await getDriverDetail(driver.id);
          const d = res.data.data;

          const loadedForm = {
            full_name: d.full_name,
            dni: d.dni,
            phone: d.phone,
            active: d.status_id === 1,
          };

          setForm(loadedForm);
          setInitialForm(loadedForm);
        } catch (err) {
          console.error("Error cargando detalle del driver", err);
        } finally {
          setLoadingDetail(false);
        }
      };

      fetchDetail();
    }, [driver, isCreating]);

    /* =========================
       HANDLERS
    ========================= */
    const handleChange = (e: any) => {
      const { name, value } = e.target;
      setForm((f) => ({ ...f, [name]: value }));
    };

    const hasChanges = () => {
      return JSON.stringify(form) !== JSON.stringify(initialForm);
    };

    useImperativeHandle(ref, () => ({
      canLeave: () => {
        if (!hasChanges()) return true;
        return window.confirm(
          "Tienes cambios sin guardar. Si continúas, se perderán. ¿Deseas continuar?"
        );
      },
    }));

    /* =========================
       ESTADOS
    ========================= */
    if (!driver && !isCreating)
      return (
        <Box
          sx={{
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#999",
            fontSize: 14,
            flexDirection: "column",
            gap: 1,
          }}
        >
          Selecciona un conductor o crea uno nuevo
        </Box>
      );

    if (loadingDetail)
      return (
        <Box sx={{ p: 3, color: "#999", fontSize: 14 }}>
          Cargando conductor...
        </Box>
      );

    /* =========================
       RENDER
    ========================= */
    return (
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        {/* HEADER */}
        <Box sx={{ p: 3, borderBottom: "1px solid #f2f2f2" }}>
          <Typography sx={{ fontSize: 18, fontWeight: 600 }}>
            {isCreating ? "Nuevo motorizado" : form.full_name}
          </Typography>
          {!isCreating && (
            <Typography sx={{ fontSize: 13, color: "#666" }}>
              ID: {driver?.id}
            </Typography>
          )}
        </Box>

        {/* BODY */}
        <Box sx={{ p: 3, flexGrow: 1, overflow: "auto" }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <Section title="Datos personales" description="Nombre completo">
              <SmallInput
                name="full_name"
                placeholder="Nombre completo"
                value={form.full_name}
                onChange={handleChange}
              />

            </Section>
            <Section description="DNI">

              <SmallInput
                name="dni"
                placeholder="Número de documento"
                value={form.dni}
                onChange={handleChange}
              />
            </Section>
            <Section description="Teléfono">
              <SmallInput
                name="phone"
                placeholder="Teléfono"
                value={form.phone}
                onChange={handleChange}
              />
            </Section>

            <Section title="Estado">
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography sx={{ fontSize: 13 }}>Activo</Typography>
                <Switch
                  size="small"
                  checked={form.active}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, active: e.target.checked }))
                  }
                />
              </Box>
            </Section>
          </Box>
        </Box>

        {/* ACTIONS */}
        <ModalActions
          onCancel={handleCancel}
          onSubmit={handleSubmit}
          submitLabel={isCreating ? "Crear motorizado" : "Guardar cambios"}
          loading={saving}
        />
      </Box>
    );
  }
);

export default DriverDetailPanel;
